import React, {useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router';
import {selectedApplicationSelector} from 'modules/applications/selectors';
import {isLoggedInSelector} from 'modules/authentication/selectors';
import ProfileMenu from 'components/App/Header/ProfileMenu';
import SidePanel from 'components/App/Header/SidePanel';
import {HOME_URL} from 'paths';
import colours from 'styles/colours';
// Logo aspect ratio: 206w x 62h  => at height 40: width ~133
const LOGO_H = 52;
const LOGO_W = Math.round(LOGO_H * (206 / 62));
const hamburgerIconStyles = {width: 20, height: 20};
export default function AppHeader() {
    const loggedIn = useSelector(isLoggedInSelector);
    const app = useSelector(selectedApplicationSelector);
    const appName = app && app.name;
    const [panelOpen, setPanelOpen] = useState(false);
    const [hamburgerHovered, setHamburgerHovered] = useState(false);
    const [logoFocused, setLogoFocused] = useState(false);
    const hamburgerRef = useRef(null);
    const logoMouseDownRef = useRef(false);
    const navigate = useNavigate();
    const goHome = e => {
        // Let the browser handle modifier/middle clicks (new tab/window).
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
        }
        e.preventDefault();
        navigate(HOME_URL);
    };
    const headerStyles = {
        display: 'flex',
        alignItems: 'center',
        height: 62,
        padding: '0 16px',
        backgroundColor: colours.background.banner,
        borderBottom: `2px solid ${colours.border.primary.highlight}`,
        boxSizing: 'border-box',
        gap: 4,
    };
    const hamburgerStyles = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        background: panelOpen || hamburgerHovered ? colours.background.bannerActive : 'transparent',
        border: 'none',
        borderRadius: 6,
        color: colours.font.contrast,
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background 0.15s',
        padding: 0,
    };
    const dividerStyles = {
        width: 1,
        height: 32,
        background: colours.border.bannerDivider,
        flexShrink: 0,
        margin: '0 10px',
    };
    const appNameStyles = {
        color: colours.font.contrast,
        fontSize: 23,
        fontWeight: 500,
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 360,
        flexShrink: 1,
        margin: 0,
        padding: 0,
        lineHeight: 1.2,
    };

    return (
        <>
            <header style={headerStyles}>
                {loggedIn && (
                    <button
                        ref={hamburgerRef}
                        style={hamburgerStyles}
                        onClick={() => setPanelOpen(o => !o)}
                        aria-label={panelOpen ? 'Close applications menu' : 'Open applications menu'}
                        aria-expanded={panelOpen}
                        aria-haspopup="dialog"
                        onMouseEnter={() => setHamburgerHovered(true)}
                        onMouseLeave={() => setHamburgerHovered(false)}
                    >
                        <img src="/images/icons/bars.svg" alt="" aria-hidden="true" style={hamburgerIconStyles}/>
                    </button>
                )}
                {loggedIn ? (
                    <a
                        href={HOME_URL}
                        onClick={goHome}
                        onMouseDown={() => { logoMouseDownRef.current = true; }}
                        onMouseUp={() => { logoMouseDownRef.current = false; }}
                        onMouseLeave={() => { logoMouseDownRef.current = false; }}
                        onFocus={() => {
                            // Only show focus ring for keyboard focus, not mouse clicks
                            if (logoMouseDownRef.current) {
                                logoMouseDownRef.current = false;
                                return;
                            }
                            setLogoFocused(true);
                        }}
                        onBlur={() => setLogoFocused(false)}
                        aria-label="Go to home"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginLeft: 8,
                            borderRadius: 4,
                            outline: logoFocused ? `2px solid ${colours.font.contrast}` : 'none',
                            outlineOffset: 2,
                            cursor: 'pointer',
                            textDecoration: 'none',
                        }}
                    >
                        <img
                            src="/images/branding/logo-lnrs-white.svg"
                            alt="LexisNexis Risk Solutions"
                            height={LOGO_H}
                            width={LOGO_W}
                            style={{flexShrink: 0, display: 'block'}}
                        />
                    </a>
                ) : (
                    <img
                        src="/images/branding/logo-lnrs-white.svg"
                        alt="LexisNexis Risk Solutions"
                        height={LOGO_H}
                        width={LOGO_W}
                        style={{flexShrink: 0, display: 'block', marginLeft: 0}}
                    />
                )}
                {loggedIn && appName && (
                    <>
                        <div style={dividerStyles} aria-hidden="true"/>
                        <h1 style={appNameStyles}>{appName}</h1>
                    </>
                )}
                <div style={{flex: 1}}/>
                {loggedIn && <ProfileMenu/>}
            </header>
            {loggedIn && <SidePanel open={panelOpen} onClose={() => setPanelOpen(false)} triggerRef={hamburgerRef}/>}
        </>
    );
}
